import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { sendReviewVerification, verifyReviewCode } from '@/api/widget'
import { EMAIL_REGEX, PHONE_REGEX } from '@/constants'
import {
  ReviewAuthChannel,
  SendReviewCodeRequest,
  SendReviewCodeResponse,
  VerifyReviewCodeRequest,
} from '@/types/widget'
import { logger, showSuccessToast, toaster } from '@/utils'
import { track } from '@/utils/analytics'
import { getStoreIDByGlobalID } from './useLocationsStore'
import { getCurrentGlobalID, getCurrentMenuType, getCurrentUID, useSessionStore } from './useSessionStore'
import { continueAfterReviewSignIn } from '@/utils/flow-navigation'

type WidgetSignInFormStoreState = {
  loading: boolean

  username: string | undefined
  phone: string | undefined
  email: string | undefined
  /**
   * Channel the shopper is actively filling (tab + last typed field).
   * Used so a leftover value in the other field cannot steal the send.
   */
  preferredChannel: ReviewAuthChannel | undefined
  response: SendReviewCodeResponse | undefined
  /**
   * The raw phone/email the shopper submitted. Kept on the store so
   * `handleVerifyCode` and `refreshReviewToken` can address the same
   * PII in the URL as the original send.
   */
  pendingPII: string | undefined
  error: string | undefined

  code: string | undefined
  codeError: string | undefined
}

type WidgetSignInFormStoreActions = {
  setUsername: (username: string) => void
  setPhone: (phone: string) => void
  setEmail: (email: string) => void
  setPreferredChannel: (channel: ReviewAuthChannel) => void
  setCode: (code: string) => void
  handleRequestCode: () => Promise<{
    success: boolean
    error: string
  }>
  handleVerifyCode: () => Promise<{
    success: boolean
    error: string
  }>
  reset: (prefix?: string) => void
  clearPendingCode: () => void
}

export type WidgetSignInFormStore = WidgetSignInFormStoreState & WidgetSignInFormStoreActions

export const initialState: WidgetSignInFormStoreState = {
  loading: false,
  username: undefined,
  phone: undefined,
  email: undefined,
  preferredChannel: undefined,
  code: undefined,
  response: undefined,
  pendingPII: undefined,
  error: '',
  codeError: undefined,
}

// Bumped by reset and clearPendingCode so an in-flight send/verify can tell
// that the form it started against is gone.
let signInAttempt = 0

function currentSignInAttempt(): number {
  return signInAttempt
}

function invalidateSignInAttempt(): void {
  signInAttempt += 1
}

export function resolveReviewAuthDestination(input: {
  username?: string
  email?: string
  phone?: string
  preferredChannel?: ReviewAuthChannel
}): { pii: string; channel: ReviewAuthChannel } | { error: string } {
  const username = input.username ?? ''
  const email = input.email ?? ''
  const phone = input.phone ?? ''
  const isUsernameValidPhone = PHONE_REGEX.test(username)
  const isUsernameValidEmail = EMAIL_REGEX.test(username)
  const isEmailValid = EMAIL_REGEX.test(email) || isUsernameValidEmail
  const isPhoneValid = PHONE_REGEX.test(phone) || isUsernameValidPhone

  if (!isEmailValid && !isPhoneValid) {
    return { error: 'Please enter a valid email address or phone number' }
  }

  const phonePii = phone || username
  const emailPii = email || username
  const preferred = input.preferredChannel

  if (preferred === 'email' && isEmailValid) {
    return { pii: emailPii, channel: 'email' }
  }
  if (preferred === 'sms' && isPhoneValid) {
    return { pii: phonePii, channel: 'sms' }
  }
  if (isEmailValid && !isPhoneValid) {
    return { pii: emailPii, channel: 'email' }
  }
  return { pii: phonePii, channel: 'sms' }
}

export const useWidgetSignInFormStore = create<WidgetSignInFormStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      setUsername: (username: string) => {
        const preferredChannel = PHONE_REGEX.test(username)
          ? 'sms'
          : EMAIL_REGEX.test(username)
            ? 'email'
            : get().preferredChannel
        set({ username, error: '', preferredChannel }, false, 'setUsername')
      },
      setPhone: (phone: string) =>
        set({ phone, error: '', ...(phone ? { preferredChannel: 'sms' as const } : {}) }, false, 'setPhone'),
      setEmail: (email: string) =>
        set({ email, error: '', ...(email ? { preferredChannel: 'email' as const } : {}) }, false, 'setEmail'),
      setPreferredChannel: (channel: ReviewAuthChannel) =>
        set({ preferredChannel: channel }, false, 'setPreferredChannel'),
      setCode: (code: string) => set({ code, error: '' }, false, 'setCode'),
      handleRequestCode: async () => {
        // Confirm and resend share this lock. A second click must not start
        // another request that can land after reset().
        if (get().loading) {
          return { success: false, error: '' }
        }

        const { username = '', email = '', phone = '', preferredChannel } = get()
        const isResend = !!get().response
        logger.log('handleRequestCode', username, email, phone)

        const uid = getCurrentUID()
        if (!uid) {
          set({ loading: false }, false, 'handleRequestCode/error')
          return { success: false, error: 'No UID found' }
        }

        const destination = resolveReviewAuthDestination({
          username,
          email,
          phone,
          preferredChannel,
        })
        if ('error' in destination) {
          set({ error: destination.error }, false, 'handleRequestCode/validationError')
          return { success: false, error: destination.error }
        }

        // BE takes :pii in the URL path; keep the raw value the user typed.
        // Server normalizes both the URL and the JWT identity claim via
        // reviewAuthIdentity(), so any casing/formatting variations match.
        const { pii, channel } = destination
        const attempt = currentSignInAttempt()

        set({ loading: true, pendingPII: pii }, false, 'handleRequestCode/start')

        const payload: SendReviewCodeRequest = { channel }
        const globalID = getCurrentGlobalID()
        const [response, error] = await sendReviewVerification(uid, pii, payload, {
          storeID: globalID ? getStoreIDByGlobalID(globalID) : undefined,
          menuType: getCurrentMenuType(),
        })

        // Verify calls reset() while this send can still be in flight.
        // Writing `response` after that opens the code screen with no pendingPII.
        if (attempt !== currentSignInAttempt() || get().pendingPII !== pii) {
          return { success: false, error: '' }
        }

        if (error) {
          set({ loading: false, error }, false, 'handleRequestCode/error')
          return { success: false, error }
        }

        set({ loading: false, response }, false, 'handleRequestCode/end')
        track('signin', isResend ? 'resend' : 'start', { method: channel })
        return { success: true, error: '' }
      },
      handleVerifyCode: async () => {
        if (get().loading) {
          return { success: false, error: '' }
        }

        const { code = '', pendingPII } = get()
        logger.log('handleVerifyCode', code)

        const uid = getCurrentUID()
        if (!uid) {
          set({ loading: false }, false, 'handleVerifyCode/error')
          return { success: false, error: 'No UID found' }
        }
        if (!pendingPII) {
          set(
            { loading: false, codeError: 'Sign-in state missing. Please request a new code.' },
            false,
            'handleVerifyCode/error'
          )
          return { success: false, error: 'Sign-in state missing' }
        }

        const attempt = currentSignInAttempt()
        set({ loading: true, codeError: undefined }, false, 'handleVerifyCode/start')

        const payload: VerifyReviewCodeRequest = { code }
        const [response, codeError] = await verifyReviewCode(uid, pendingPII, payload)
        if (attempt !== currentSignInAttempt() || get().pendingPII !== pendingPII) {
          return { success: false, error: '' }
        }

        if (codeError) {
          set({ loading: false, codeError }, false, 'handleVerifyCode/error')
          return { success: false, error: codeError }
        }

        if (!response?.token || !response?.expiresAt) {
          const msg = 'Verification response missing token'
          set({ loading: false, codeError: msg }, false, 'handleVerifyCode/error')
          return { success: false, error: msg }
        }

        useSessionStore.getState().setReviewAuth({
          token: response.token,
          expiresAt: response.expiresAt,
          pii: pendingPII,
        })

        continueAfterReviewSignIn()

        showSuccessToast('Signed in successfully')

        // Reset form fields but keep loading=false so the code screen unmounts cleanly.
        get().reset('handleVerifyCode/reset')

        track('signin', 'confirm', {})
        return { success: true, error: '' }
      },
      reset: (prefix = 'reset') => {
        invalidateSignInAttempt()
        set(initialState, false, prefix)
      },
      clearPendingCode: () => {
        invalidateSignInAttempt()
        set(
          {
            response: undefined,
            pendingPII: undefined,
            code: undefined,
            codeError: undefined,
            error: '',
            loading: false,
          },
          false,
          'clearPendingCode'
        )
      },
    }),
    { name: 'WidgetSignInFormStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

/**
 * Utility function to check if a code has been sent.
 * Uses the `sent` flag on the new SendReviewCodeResponse contract.
 */
export function hasSentCode(): boolean {
  const { response, pendingPII } = useWidgetSignInFormStore.getState()
  // `response` without `pendingPII` is the broken confirm screen: verify and
  // resend both fail until the shopper backs out.
  return !!response?.sent && !!pendingPII
}

/**
 * Utility function to handle the code request and verify
 * @returns void
 */
function toastSignInError(success: boolean, error: string): void {
  // Empty error means the click was ignored because the other action is in flight.
  if (success || error === '') return
  toaster?.addToast?.({
    type: 'danger',
    message: error || 'Something went wrong, please try again',
  })
}

export async function handleCodeRequestAndVerify(): Promise<void> {
  const state = useWidgetSignInFormStore.getState()
  if (state.loading) return

  const hasCode = !!state.response?.sent && !!state.pendingPII
  if (hasCode) {
    const { success, error } = await state.handleVerifyCode()
    toastSignInError(success, error)
    return
  }

  const { success, error } = await state.handleRequestCode()
  toastSignInError(success, error)
}

export async function handleResendCode(): Promise<void> {
  if (useWidgetSignInFormStore.getState().loading) return

  const { success, error } = await useWidgetSignInFormStore.getState().handleRequestCode()
  toastSignInError(success, error)
}
